import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { IRELAND } from '@nxsys/store/constants/keys';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { PSR_SUBMIT_TYPE } from '@nxsys/constants/psr';
import { Employment, Payroll } from '@nxsys/store/slices/revenueFilings/types';

export const getColumns = ({
  messages,
  psrSubmitType,
}: {
  messages: TypedMessages;
  psrSubmitType: (typeof PSR_SUBMIT_TYPE)[keyof typeof PSR_SUBMIT_TYPE];
}): ColumnsType<Payroll | Employment> => {
  if (psrSubmitType === PSR_SUBMIT_TYPE.NORMAL) {
    return [
      {
        key: 'id',
        title: messages['common.employeeId'],
        dataIndex: 'subcontractorRef',
        render: (item) => item?.id ?? '',
      },
      {
        key: 'subcontractorName',
        title: messages['common.employeeName'],
        dataIndex: 'subcontractorName',
      },
      {
        key: 'company',
        title: messages['common.employer'],
        dataIndex: 'companyRef',
        render: (record) => record?.companyName ?? '',
      },
      {
        key: 'taxPeriod',
        title: messages['table.taxPeriod'],
        render: (record) =>
          record?.payFrequency === 'MONTHLY'
            ? record?.monthConfig
            : record?.taxPeriod || '-',
      },
      {
        key: 'payFrequency',
        title: messages['table.payFrequency'],
        dataIndex: 'payFrequency',
        render: (record) =>
          defaultPaymentFrequency.find((c) => c.value === record)?.label ?? '',
      },
      {
        key: 'dateLeft',
        title: messages['table.dateLeft'],
        dataIndex: 'subcontractorRef',
        align: 'right',
        render: (record) =>
          record?.dateLeft ? moment(record.dateLeft).format('DD/MM/YYYY') : '',
      },
      {
        key: 'tax',
        title: messages['table.tax'],
        dataIndex: 'irelandValue',
        align: 'right',
        render: (record) =>
          formatMoney(record?.tax || 0, showCurrencyByCountry(IRELAND)),
      },
      {
        key: 'prsi',
        title: messages['table.prsi'],
        dataIndex: 'irelandValue',
        align: 'right',
        render: (record) =>
          formatMoney(
            record?.eePRSI || 0 + record?.erPRSI || 0,
            showCurrencyByCountry(IRELAND),
          ),
      },
      {
        key: 'usc',
        title: messages['table.usc'],
        dataIndex: 'irelandValue',
        align: 'right',
        render: (record) =>
          formatMoney(record?.usc || 0, showCurrencyByCountry(IRELAND)),
      },
      {
        key: 'lpt',
        title: messages['table.lpt'],
        dataIndex: 'irelandValue',
        align: 'right',
        render: (record) =>
          formatMoney(record?.lptDeducted || 0, showCurrencyByCountry(IRELAND)),
      },
      {
        key: 'status',
        title: messages['common.status'],
        dataIndex: 'subcontractorRef',
        render: (record) =>
          !record?.hasSubmittedPSR
            ? 'PSR not yet sent to Revenue'
            : 'PSR sent to Revenue',
        width: 250,
      },
    ];
  }

  if (psrSubmitType === PSR_SUBMIT_TYPE.LEAVER) {
    return [
      {
        key: 'employee',
        title: messages['common.employeeName'],
        render: (record: Employment) => record?.subcontractor?.fullName || '_',
        width: 200,
      },
      {
        key: 'employer',
        title: messages['common.employer'],
        render: (record: Employment) =>
          record?.companyPayment?.companyName || '_',
        width: 200,
      },
      {
        key: 'taxPeriod',
        title: messages['table.taxPeriod'],
        render: (record: Employment) => {
          // Use the tax period stored on the employment: it is calculated by the
          // backend against the payroll country's tax calendar. Deriving it here
          // from dateLeft would always apply the UK calendar (6 Apr start) and
          // show the wrong week for Ireland (1 Jan start).
          return record?.taxPeriodLeaver ?? '_';
        },
      },
      {
        key: 'payFrequency',
        title: messages['table.payFrequency'],
        render: (record: Employment) => {
          const value = record?.subcontractor?.payFrequency;
          return (
            defaultPaymentFrequency.find((c) => c.value === value)?.label || '_'
          );
        },
      },
      {
        key: 'dateLeft',
        title: messages['table.dateLeft'],
        align: 'right',
        render: (record: Employment) =>
          record?.dateLeft ? moment(record.dateLeft).format('DD/MM/YYYY') : '_',
      },
      {
        key: 'tax',
        title: messages['table.tax'],
        align: 'right',
        render: () => formatMoney(0, showCurrencyByCountry(IRELAND)),
        width: 200,
      },
      {
        key: 'prsi',
        title: messages['table.prsi'],
        align: 'right',
        render: () => formatMoney(0, showCurrencyByCountry(IRELAND)),
        width: 200,
      },
      {
        key: 'usc',
        title: messages['table.usc'],
        align: 'right',
        render: () => formatMoney(0, showCurrencyByCountry(IRELAND)),
        width: 200,
      },
      {
        key: 'lpt',
        title: messages['table.lpt'],
        align: 'right',
        render: () => formatMoney(0, showCurrencyByCountry(IRELAND)),
        width: 200,
      },
    ];
  }

  return [];
};
